import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';

const FlexFieldValues = ({ entityType, entityId, compact = false }) => {
  const { token } = useContext(AppContext);
  const [definitions, setDefinitions] = useState([]);
  const [values, setValues] = useState({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchDefinitions();
    if (entityId) fetchValues();
  }, [entityType, entityId]);

  const fetchDefinitions = async () => {
    try {
      const response = await fetch(`/api/flex-fields/definitions?entityType=${entityType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDefinitions(data);
      }
    } catch (error) {
      console.error('Error fetching flex field definitions:', error);
    }
  };

  const fetchValues = async () => {
    try {
      const response = await fetch(`/api/flex-fields/values/${entityType}/${entityId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const valueMap = {};
        data.forEach(v => { valueMap[v.definitionId] = v.value; });
        setValues(valueMap);
      }
    } catch (error) {
      console.error('Error fetching flex field values:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const valueArray = definitions.map(def => ({
        definitionId: def.id,
        value: values[def.id] || ''
      }));

      await fetch(`/api/flex-fields/values/${entityType}/${entityId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ values: valueArray })
      });
      setEditing(false);
    } catch (error) {
      console.error('Error saving flex field values:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (definitionId, value) => {
    setValues(prev => ({ ...prev, [definitionId]: value }));
  };

  if (definitions.length === 0) return null;

  const renderField = (def) => {
    const value = values[def.id] || '';

    if (!editing) {
      if (!value && !def.isRequired) return null;
      return (
        <div key={def.id} className={compact ? 'mb-1' : 'mb-2'}>
          <span className="text-xs text-gray-500">{def.fieldLabel}: </span>
          <span className="text-sm text-gray-900">
            {def.fieldType === 'checkbox' ? (value === 'true' ? 'Yes' : 'No') : (value || '-')}
          </span>
        </div>
      );
    }

    return (
      <div key={def.id} className="mb-3">
        <label className="block text-xs font-medium text-gray-600 mb-1">
          {def.fieldLabel}
          {def.isRequired && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        {def.fieldType === 'text' && (
          <input
            type="text"
            value={value}
            onChange={e => handleChange(def.id, e.target.value)}
            className="w-full px-2 py-1.5 text-sm border rounded-md focus:ring-brand-green-500 focus:border-brand-green-500"
          />
        )}
        {def.fieldType === 'number' && (
          <input
            type="number"
            value={value}
            onChange={e => handleChange(def.id, e.target.value)}
            className="w-full px-2 py-1.5 text-sm border rounded-md focus:ring-brand-green-500 focus:border-brand-green-500"
          />
        )}
        {def.fieldType === 'date' && (
          <input
            type="date"
            value={value}
            onChange={e => handleChange(def.id, e.target.value)}
            className="w-full px-2 py-1.5 text-sm border rounded-md focus:ring-brand-green-500 focus:border-brand-green-500"
          />
        )}
        {def.fieldType === 'textarea' && (
          <textarea
            value={value}
            onChange={e => handleChange(def.id, e.target.value)}
            rows={2}
            className="w-full px-2 py-1.5 text-sm border rounded-md focus:ring-brand-green-500 focus:border-brand-green-500"
          />
        )}
        {def.fieldType === 'select' && (
          <select
            value={value}
            onChange={e => handleChange(def.id, e.target.value)}
            className="w-full px-2 py-1.5 text-sm border rounded-md focus:ring-brand-green-500 focus:border-brand-green-500"
          >
            <option value="">-- Select --</option>
            {(def.options || []).map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        )}
        {def.fieldType === 'checkbox' && (
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={value === 'true'}
              onChange={e => handleChange(def.id, String(e.target.checked))}
              className="h-4 w-4 text-brand-green-600 rounded"
            />
            <span className="text-sm text-gray-700">Yes</span>
          </label>
        )}
      </div>
    );
  };

  const hasValues = definitions.some(def => values[def.id]);

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-gray-700">Additional Fields</h4>
        {entityId && (
          <button
            type="button"
            onClick={() => editing ? handleSave() : setEditing(true)}
            disabled={saving}
            className="text-xs text-brand-green-600 hover:text-brand-green-700"
          >
            {saving ? 'Saving...' : editing ? 'Save' : 'Edit'}
          </button>
        )}
      </div>

      {editing ? (
        <div className="bg-gray-50 rounded-lg p-3 border">
          {definitions.map(renderField)}
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-1.5 text-xs bg-brand-green-600 text-white rounded-md hover:bg-brand-green-700 disabled:bg-gray-300"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); fetchValues(); }}
              className="flex-1 py-1.5 text-xs bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div>
          {definitions.map(renderField)}
          {!hasValues && (
            <p className="text-xs text-gray-400">No additional data</p>
          )}
        </div>
      )}
    </div>
  );
};

export default FlexFieldValues;
